import { useEffect } from "react";
import { useLocation } from "react-router";
import { getBranding } from "../../config/brandingConfig";

// Per-route ProSchool SEO values (from SEO Activities xlsx)
const SEO = {
  "/": {
    title: "AI-Powered Educational Platform for Online Learning | ProSchool",
    desc: "ProSchool is an AI powered educational platform and online learning management system built to help institutions manage courses, learners, assessments, and digital learning experiences.",
    canonical: true,
  },
  "/about": {
    title: "About ProSchool – AI Learning Management System",
    desc: "Learn about ProSchool, an AI-driven learning management system designed for schools, institutions, and training providers looking to deliver smarter online education.",
    canonical: true,
  },
  "/signup": {
    title: "Sign Up for ProSchool | Start Using Our Online LMS",
    desc: "Create your ProSchool account and start using an AI powered online learning management system for digital courses, learners, and education workflows.",
    canonical: false, // xlsx marks canonical as "-"
  },
  "/login": {
    title: "ProSchool Login | Access Your Online Learning Platform",
    desc: "Log in to ProSchool to access your online learning platform, manage courses, continue lessons, and track your learning progress.",
    canonical: false,
  },
  "/privacy-policy": {
    title: "Privacy Policy | ProSchool",
    desc: "Read ProSchool's Privacy Policy to understand how we collect, use, protect, and manage user data across our online learning platform.",
    canonical: true,
  },
  "/terms-and-conditions": {
    title: "Terms and Conditions | ProSchool",
    desc: "Review ProSchool's Terms and Conditions for using our online learning platform, services, website, and related features.",
    canonical: true,
  },
};

  // Fallback for any route not explicitly listed above (dashboard, course view, etc.).
  // Ensures unmatched/authenticated pages always get a defined title + description
  // instead of inheriting a stale one from the previous route or index.html.
  const DEFAULT_SEO = {
    title: "AI-Powered Educational Platform for Online Learning | ProSchool",
    desc: "ProSchool is an AI powered educational platform and online learning management system built to help institutions manage courses, learners, assessments, and digital learning experiences.",
    canonical: false, // authenticated/app pages shouldn't be canonicalized
  };

// Find an existing head tag or create + append a new one.
const upsertTag = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
};

const SeoHead = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    let cancelled = false;
    let titleObserver;

    // Normalize trailing slash so "/about/" still matches "/about".
    const path =
      pathname !== "/" && pathname.endsWith("/")
        ? pathname.slice(0, -1)
        : pathname;

    // Any route not in SEO falls back to the default ProSchool title/desc.
    const cfg = SEO[path] ?? DEFAULT_SEO;

    let branding;
    try {
      branding = getBranding(window.location.hostname);
    } catch (err) {
      // Fail safe: never let SEO logic break the app.
      console.error("SeoHead: branding resolve failed", err);
      return;
    }

    // ---- ONLY run for ProSchool ----
    if (branding?.variant !== "proschool") return;

    // (a) Title
    if (cfg.title) {
      document.title = cfg.title;
    }

    // (b) Meta description
    if (cfg.desc) {
      const meta = upsertTag("meta[name='description']", () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        return m;
      });
      meta.setAttribute("content", cfg.desc);
    }

    // (c) Canonical
    const existingCanonical = document.head.querySelector(
      "link[rel='canonical']",
    );
    if (cfg.canonical) {
      const link =
        existingCanonical ||
        upsertTag("link[rel='canonical']", () => {
          const l = document.createElement("link");
          l.setAttribute("rel", "canonical");
          return l;
        });
      link.setAttribute(
        "href",
        `https://proschool.ai${path === "/" ? "/" : path}`,
      );
    } else if (existingCanonical) {
      // signup / login / any unmatched app route: no canonical
      existingCanonical.remove();
    }

    // (d) Organization JSON-LD schema (sameAs built from branding -> never drifts)
    const ld = upsertTag("script#org-schema", () => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "org-schema";
      return s;
    });
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      name: branding.fullName,
      url: "https://proschool.ai/",
      description:
        "ProSchool is an AI powered educational platform and online learning management system built to help institutions manage courses, learners, assessments, and digital learning experiences.",
      email: branding.supportEmail,
      address: {
        "@type": "PostalAddress",
        streetAddress: "18 West 18th Street",
        addressLocality: "New York",
        addressRegion: "NY",
        postalCode: "10011",
        addressCountry: "US",
      },
      sameAs: Object.values(branding.social || {}).filter(Boolean),
      contactPoint: {
        "@type": "ContactPoint",
        email: branding.supportEmail,
        contactType: "customer support",
        availableLanguage: "English",
      },
    });

    // (e) DEFENSIVE: on a hard refresh a competing title manager (e.g. react-helmet,
    // or a stray document.title write in a layout) runs *after* this effect and
    // overwrites the title. Watch the <title> element and re-assert our value
    // whenever it diverges. Re-setting to our own value won't loop, because the
    // guard skips when it already matches.
    if (cfg.title) {
      const titleEl = document.querySelector("title");
      if (titleEl && typeof MutationObserver !== "undefined") {
        titleObserver = new MutationObserver(() => {
          if (cancelled) return;
          if (document.title !== cfg.title) {
            document.title = cfg.title;
          }
        });
        titleObserver.observe(titleEl, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
    }

    // Stop defending when the route changes or the component unmounts.
    return () => {
      cancelled = true;
      if (titleObserver) titleObserver.disconnect();
    };
  }, [pathname]);

  return null;
};

export default SeoHead;